/**
 * The warm-up calculator (`D209`, B2) — PURE.
 *
 * ── THREE RAMPS, FROM THE FIRST WORKING SET'S LOAD ──────────────────────────
 * - Clássico: 40 % × 5, 60 % × 5, 80 % × 3 — the general ramp.
 * - Força: 40 % × 5, 60 % × 3, 80 % × 2, 90 % × 1 — fewer reps as it climbs,
 *   so a heavy top set is not preceded by fatigue.
 * - Rampa com barra: the empty bar × 5, then 40 % × 5, 60 % × 3, 80 % × 2.
 *
 * ── ROUNDED TO WHAT CAN BE LOADED ──────────────────────────────────────────
 * Each step is rounded to the nearest load the person can actually make:
 * the bar and the plates they have (`plate-math.ts`, both sides), dumbbell
 * steps of 2 kg, or the exercise's own load step on a machine. A step that
 * rounds to the previous step's load, or up to the working load itself, is
 * dropped — two identical warm-ups, or a "warm-up" that is the working set,
 * are not what the person asked for.
 *
 * The plan item's `PlanSessionWarmup` (`D103`) is the engine's minutes and
 * ramp-up percentages for the SESSION; this is the person's own ramp for
 * one exercise, logged as sets.
 */
import { kgToLb, lbToKg, loadStepInUnit } from "@repo/client/format";
import { nearestLoadable } from "../plates/plate-math";
import type { PlannedWarmup } from "../store/session-model";

export type WarmupMethod = "classic" | "strength" | "ramp";

export const WARMUP_METHODS: readonly WarmupMethod[] = [
  "classic",
  "strength",
  "ramp",
];

interface Step {
  /** Percent of the working load; `null` is the empty bar. */
  readonly pct: number | null;
  readonly reps: number;
}

const STEPS: Readonly<Record<WarmupMethod, readonly Step[]>> = {
  classic: [
    { pct: 40, reps: 5 },
    { pct: 60, reps: 5 },
    { pct: 80, reps: 3 },
  ],
  ramp: [
    { pct: null, reps: 5 },
    { pct: 40, reps: 5 },
    { pct: 60, reps: 3 },
    { pct: 80, reps: 2 },
  ],
  strength: [
    { pct: 40, reps: 5 },
    { pct: 60, reps: 3 },
    { pct: 80, reps: 2 },
    { pct: 90, reps: 1 },
  ],
};

/** How a step's load is made loadable. */
export type WarmupRounding =
  | {
      /** The bar, in {@link unit} — kilograms unless it says otherwise. */
      readonly barKg: number;
      readonly kind: "plates";
      /** The plates, in {@link unit}. */
      readonly plates: readonly number[];
      /**
       * `lb` in a pound gym (`D266`): the bar and the plates are pounds, the
       * warm-up's own loads stay kilograms, and only the rounding crosses.
       */
      readonly unit?: "kg" | "lb";
    }
  | {
      readonly kind: "dumbbell";
      readonly stepKg: number;
      /** `lb` rounds to the pound step nearest `stepKg` (`D266`). */
      readonly unit?: "kg" | "lb";
    }
  | {
      readonly kind: "step";
      readonly stepKg: number;
      /** `lb` rounds to the pound step nearest `stepKg` (`D266`). */
      readonly unit?: "kg" | "lb";
    };

/** The dumbbell rack's step, when the settings choose dumbbells. */
export const DUMBBELL_STEP_KG = 2;

/**
 * The catalogue's bar equipment (`EQUIPMENT_GROUPS`, `@repo/contract`):
 * Barbell, EZ Barbell, Smith machine, Special Bar and Trap bar — what the
 * plate calculator is offered for and the "automatic" rounding loads plates
 * on. Ids, never names: the names are localised.
 */
export const BAR_EQUIPMENT_IDS: readonly number[] = [3, 8, 20, 21, 28];

/** Dumbbell and Kettlebell: loaded from a rack in steps, one per hand. */
const RACK_EQUIPMENT_IDS: readonly number[] = [7, 11];

/** The step a machine's warm-up rounds to when the plan names none. */
const DEFAULT_STEP_KG = 2.5;

/**
 * The settings' rounding choice made concrete for one exercise. "Automático"
 * reads the equipment: a bar loads plates, a dumbbell, kettlebell or any
 * per-hand load steps by 2 kg, and anything else (a stack, a machine) by the
 * plan's load step.
 */
export function resolveRounding(
  choice: "auto" | "dumbbell" | "plates",
  exercise: {
    readonly barKg: number;
    readonly equipmentId: number;
    readonly loadStepKg: number | undefined;
    readonly perHand: boolean;
    readonly plates: readonly number[];
    /** The unit `barKg` and `plates` are in (`D266`); kilograms if absent. */
    readonly unit?: "kg" | "lb";
  }
): WarmupRounding {
  const plates: WarmupRounding = {
    barKg: exercise.barKg,
    kind: "plates",
    plates: exercise.plates,
    ...(exercise.unit === "lb" ? { unit: "lb" as const } : {}),
  };
  const lb = exercise.unit === "lb" ? { unit: "lb" as const } : {};
  const dumbbell: WarmupRounding = {
    kind: "dumbbell",
    stepKg: DUMBBELL_STEP_KG,
    ...lb,
  };
  if (choice === "plates") {
    return plates;
  }
  if (choice === "dumbbell") {
    return dumbbell;
  }
  if (BAR_EQUIPMENT_IDS.includes(exercise.equipmentId)) {
    return plates;
  }
  if (exercise.perHand || RACK_EQUIPMENT_IDS.includes(exercise.equipmentId)) {
    return dumbbell;
  }
  return {
    kind: "step",
    stepKg: exercise.loadStepKg ?? DEFAULT_STEP_KG,
    ...lb,
  };
}

function roundTo(value: number, step: number): number {
  return Math.max(step, Math.round(value / step) * step);
}

/**
 * A step rounding in the person's unit (`D266`): in pounds the load is
 * rounded on the pound grid a rack has (`loadStepInUnit` — 2 kg → 5 lb) and
 * comes back as kilograms, so the warm-up reads 45, 65, 95 lb and not 44.1.
 */
function roundInUnit(
  kg: number,
  stepKg: number,
  unit: "kg" | "lb" | undefined
): number {
  if (unit !== "lb") {
    return roundTo(kg, stepKg);
  }
  return lbToKg(roundTo(kgToLb(kg), loadStepInUnit(stepKg, "lb")));
}

function loadable(kg: number, rounding: WarmupRounding): number {
  switch (rounding.kind) {
    case "plates":
      return rounding.unit === "lb"
        ? lbToKg(nearestLoadable(kgToLb(kg), rounding.barKg, rounding.plates))
        : nearestLoadable(kg, rounding.barKg, rounding.plates);
    case "dumbbell":
      return roundInUnit(kg, rounding.stepKg, rounding.unit);
    default:
      return roundInUnit(kg, rounding.stepKg, rounding.unit);
  }
}

/**
 * The warm-up sets before a first working set of `workingKg`, lightest
 * first. Empty when there is nothing to ramp to (no load, or one so light
 * every step rounds onto it).
 */
export function warmupSets(input: {
  readonly method: WarmupMethod;
  readonly rounding: WarmupRounding;
  readonly workingKg: number | null;
}): PlannedWarmup[] {
  const { method, rounding, workingKg } = input;
  if (workingKg === null || workingKg <= 0) {
    return [];
  }
  const out: PlannedWarmup[] = [];
  for (const step of STEPS[method]) {
    let load: number;
    if (step.pct === null) {
      // The empty bar exists only on a bar; with dumbbells there is none.
      if (rounding.kind !== "plates") {
        continue;
      }
      // A pound bar is pounds (`D266`); the warm-up's loads are kilograms.
      load = rounding.unit === "lb" ? lbToKg(rounding.barKg) : rounding.barKg;
    } else {
      load = loadable((workingKg * step.pct) / 100, rounding);
    }
    const previous = out.at(-1)?.load ?? null;
    if (load >= workingKg || (previous !== null && load <= previous)) {
      continue;
    }
    out.push({ load, reps: step.reps });
  }
  return out;
}
